import React, { useState, useReducer, useContext, useEffect } from "react";
import { loginReducer } from "pages/login/login.reducer";
import { LoginResult } from "models/login.model";
import { AppContext } from "./app.context";
import { getDomainStorage, getLoginStorage, LoginStorage, removeLoginStorage, setLoginStorage } from "misc/utils";
import { HttpStatusCodeEnum } from "models/http-status-code.enum";
import { instance as api } from "misc/phqHTTP";
import { Domain } from "models/domain.model";
import jwt_decode from "jwt-decode";
import { APIResponse } from "models/api-response.model";

export const AuthContext = React.createContext({
  isAuth: false,
  logInfo: {} as LoginResult,
  login: async(username: string, password: string):Promise<boolean|void> => {},
  logout: () => {},
  loginWStorage: async() => {},
});

const initialLoginInfo = {} as LoginResult;

const AuthContextProvider = (props: { children: React.ReactNode }) => {
  const { notifyError, setDomain, setIsLoading, domain } = useContext(AppContext);
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [loginInfo, dispatch] = useReducer(loginReducer, initialLoginInfo);

  const logout = () => {
    delete api.defaults.headers.Authorization;
    setAuthenticated(false);
    removeLoginStorage();
  };

  const loginHandler = async (username: string, password: string) => {
    setIsLoading(true);
    const domainCode = domain.Code;
    return api
      .post(`/user/authenticate`, { username, password, domainCode })
      .then((res) => {
        if (res.status === HttpStatusCodeEnum.OK) {
          const {result: loginResult } = res.data as APIResponse<LoginResult>;

          var decoded = jwt_decode(loginResult.AccessToken) as {[x:string]: string}
          console.log({decoded})
          const roleKey = Object.keys(decoded).find((key) => key.endsWith('role'));
          if(roleKey) {
            loginResult.Role = decoded[roleKey];
          }

          // Alter defaults after instance has been created
          const storagePayload: LoginStorage = {
            domain,
            loginResult
          }

          setDomain(domain);
          setLoginStorage(storagePayload);

          api.defaults.headers.Authorization = `Bearer ${loginResult.AccessToken}`;
          dispatch({ type: "set", value: loginResult });
          setAuthenticated(true);
          
          return true;
        } else {
          notifyError(res.data.message);
          return false;
        }
      })
      .catch(() => notifyError("Login failed."))
      .finally(() => setIsLoading(false));
  };

  const tryLoginWStorageHandler = async() => {
    const loginStorage = await getLoginStorage();

    if(loginStorage?.domain) {
      setDomain(loginStorage.domain);
    }
    else {
      const domains: Array<Domain> = await getDomainStorage();
      if(domains.length > 0) {
          setDomain(domains[0])
      }
    }

    if(loginStorage?.loginResult) {
      setAuthenticated(true);

      const { AccessToken } = loginStorage.loginResult;
      api.defaults.headers.Authorization = `Bearer ${AccessToken}`;
      dispatch({ type: "set", value: loginStorage.loginResult });
    }
  };

  useEffect(() => {
    tryLoginWStorageHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuth: isAuthenticated,
        logInfo: loginInfo,
        login: loginHandler,
        logout: logout,
        loginWStorage: tryLoginWStorageHandler,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;

